
import React, { useState, useRef } from 'react';
import { ReferenceAsset, AppSettings } from '../types';
import { fileToBase64 } from '../utils/helpers';
import { generateImage } from '../services/geminiService';
import { Upload, Sparkles, X, Check, RefreshCw, Image as ImageIcon, Loader2, Cpu } from 'lucide-react';

interface AssetImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAssetsAdded: (assets: ReferenceAsset[]) => void;
  settings: AppSettings;
}

type Tab = 'upload' | 'generate';
type AssetType = ReferenceAsset['type'];

const AssetImportModal: React.FC<AssetImportModalProps> = ({ isOpen, onClose, onAssetsAdded, settings }) => {
  const [activeTab, setActiveTab] = useState<Tab>('upload');
  
  // -- Upload State --
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadType, setUploadType] = useState<AssetType>('character');
  const [isProcessingFiles, setIsProcessingFiles] = useState(false);

  // -- Generate State --
  const [prompt, setPrompt] = useState('');
  const [genType, setGenType] = useState<AssetType>('character');
  const [genModel, setGenModel] = useState<AppSettings['imageModel']>(settings.imageModel);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedResult, setGeneratedResult] = useState<{ base64: string; mimeType: string } | null>(null);

  if (!isOpen) return null;

  const getTypeLabel = (type: string) => {
      switch(type) {
          case 'character': return '角色';
          case 'scene': return '场景';
          case 'prop': return '道具';
          case 'style': return '风格';
          default: return type;
      }
  }

  // --- Upload Logic ---
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setIsProcessingFiles(true);
      const newAssets: ReferenceAsset[] = [];
      // Fix: Cast Array.from result to File[] to ensure 'file' has the correct type in the loop
      const files = Array.from(e.target.files) as File[];
      
      try {
        for (const file of files) {
          const base64 = await fileToBase64(file);
          const nameWithoutExt = file.name.replace(/\.[^/.]+$/, "");
          newAssets.push({
            id: Math.random().toString(36).substr(2, 9),
            name: nameWithoutExt,
            keywords: nameWithoutExt,
            type: uploadType,
            url: URL.createObjectURL(file),
            base64: base64,
            mimeType: file.type
          });
        }
        onAssetsAdded(newAssets);
        onClose();
      } catch (err) {
        console.error("Batch upload failed:", err);
        alert("部分文件上传失败，请重试。");
      } finally {
        setIsProcessingFiles(false);
      }
    }
  };

  // --- AI Generate Logic ---
  const handleGenerate = async () => {
    if (!prompt.trim()) return;

    setIsGenerating(true);
    setGeneratedResult(null);

    try {
      // Create a temporary settings object with the selected model
      const tempSettings = { ...settings, imageModel: genModel };
      
      const result = await generateImage(
        prompt, 
        [], // No references, pure text-to-image
        '1K', // Default resolution for assets
        '1:1', // Default square aspect ratio
        tempSettings
      );
      setGeneratedResult(result);
    } catch (error) {
      console.error(error);
      alert("生成失败，请检查 API 配置或提示词。");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSaveGenerated = () => {
    if (!generatedResult) return;

    const newAsset: ReferenceAsset = {
      id: Math.random().toString(36).substr(2, 9),
      name: prompt.slice(0, 20).trim() || 'AI 生成资源', 
      keywords: prompt.split(' ').slice(0, 3).join('|'), 
      type: genType,
      url: `data:${generatedResult.mimeType};base64,${generatedResult.base64}`,
      base64: generatedResult.base64,
      mimeType: generatedResult.mimeType
    };

    onAssetsAdded([newAsset]);
    setGeneratedResult(null);
    setPrompt('');
  };

  const getModelLabel = (modelId: string) => {
    if (settings.apiProvider === 'grsai') {
        return modelId === 'gemini-3-pro-image-preview' ? 'Banana Pro' : 'Banana Fast';
    }
    if (settings.apiProvider === 'siliconflow') {
        return 'Qwen Edit';
    }
    return modelId === 'gemini-3-pro-image-preview' ? 'Pro 3.0' : 'Flash 2.5';
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <div className="bg-slate-900 rounded-xl border border-slate-700 w-full max-w-lg shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-800 bg-slate-900">
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <ImageIcon className="w-5 h-5 text-indigo-400" />
            导入参考资源
          </h3>
          <button onClick={onClose} className="p-2 hover:bg-slate-800 rounded-full text-slate-400 hover:text-white transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-800">
          <button 
            onClick={() => setActiveTab('upload')}
            className={`flex-1 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
              activeTab === 'upload' ? 'bg-slate-800 text-white border-b-2 border-indigo-500' : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'
            }`}
          >
            <Upload size={16} /> 批量上传
          </button>
          <button 
            onClick={() => setActiveTab('generate')}
            className={`flex-1 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
              activeTab === 'generate' ? 'bg-slate-800 text-white border-b-2 border-indigo-500' : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'
            }`}
          >
            <Sparkles size={16} /> AI 生成
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          
          {/* --- TAB: UPLOAD --- */}
          {activeTab === 'upload' && (
            <div className="space-y-6">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-300">分类到</label>
                <div className="flex gap-2">
                  {(['character', 'scene', 'prop', 'style'] as AssetType[]).map(t => (
                    <button
                      key={t}
                      onClick={() => setUploadType(t)}
                      className={`flex-1 py-2 text-[10px] uppercase font-bold rounded border transition-all ${
                        uploadType === t 
                        ? 'bg-indigo-600 border-indigo-500 text-white' 
                        : 'bg-slate-950 border-slate-800 text-slate-500 hover:border-slate-600'
                      }`}
                    >
                      {getTypeLabel(t)}
                    </button>
                  ))}
                </div>
              </div>

              <div 
                onClick={() => !isProcessingFiles && fileInputRef.current?.click()}
                className={`border-2 border-dashed border-slate-700 rounded-xl p-10 flex flex-col items-center justify-center text-slate-500 hover:border-indigo-500 hover:text-indigo-400 hover:bg-slate-800/30 transition-all cursor-pointer ${isProcessingFiles ? 'opacity-50 cursor-wait' : ''}`}
              >
                {isProcessingFiles ? (
                    <Loader2 size={40} className="mb-4 animate-spin text-indigo-500" />
                ) : (
                    <Upload size={40} className="mb-4" />
                )}
                <p className="text-sm font-medium">{isProcessingFiles ? '正在处理文件...' : '点击或拖拽上传图片'}</p>
                <p className="text-xs mt-1 opacity-70">支持批量选择 JPG, PNG</p>
                <input 
                  type="file" 
                  multiple 
                  accept="image/*" 
                  className="hidden" 
                  ref={fileInputRef} 
                  onChange={handleFileChange}
                />
              </div>
            </div>
          )}

          {/* --- TAB: GENERATE --- */}
          {activeTab === 'generate' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-300">资源类型</label>
                  <select 
                    value={genType} 
                    onChange={(e) => setGenType(e.target.value as AssetType)}
                    className="w-full bg-slate-950 border border-slate-700 rounded-lg p-2 text-xs text-slate-200 outline-none focus:border-indigo-500"
                  >
                    <option value="character">角色</option>
                    <option value="scene">场景</option>
                    <option value="prop">道具</option>
                    <option value="style">风格</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-300 flex items-center gap-1">
                    <Cpu size={12} /> 生成模型
                  </label>
                  <select 
                    value={genModel} 
                    onChange={(e) => setGenModel(e.target.value as any)}
                    className="w-full bg-slate-950 border border-slate-700 rounded-lg p-2 text-xs text-slate-200 outline-none focus:border-indigo-500"
                  >
                    <option value="gemini-2.5-flash-image">{getModelLabel('gemini-2.5-flash-image')}</option>
                    <option value="gemini-3-pro-image-preview">{getModelLabel('gemini-3-pro-image-preview')}</option>
                  </select>
                </div>
              </div>

              <div className="space-y-2">
                 <label className="text-sm font-medium text-slate-300">提示词</label>
                 <textarea
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder={`描述想要生成的${getTypeLabel(genType)}...`}
                    className="w-full h-24 bg-slate-950 border border-slate-700 rounded-lg p-3 text-sm text-slate-200 focus:ring-2 focus:ring-indigo-500 outline-none resize-none"
                 />
              </div>

              {/* Preview Area */}
              {generatedResult ? (
                <div className="space-y-3 pt-2">
                    <div className="aspect-square w-full bg-black rounded-lg overflow-hidden border border-slate-700 relative">
                        <img 
                            src={`data:${generatedResult.mimeType};base64,${generatedResult.base64}`} 
                            alt="Generated Asset" 
                            className="w-full h-full object-contain"
                        />
                    </div>
                    <div className="flex gap-3">
                        <button 
                            onClick={handleGenerate}
                            className="flex-1 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-600 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors"
                        >
                            <RefreshCw size={16} /> 重新生成
                        </button>
                        <button 
                            onClick={handleSaveGenerated}
                            className="flex-1 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 shadow-lg shadow-emerald-900/20 transition-colors"
                        >
                            <Check size={16} /> 保存到库
                        </button>
                    </div>
                </div>
              ) : (
                <button
                    onClick={handleGenerate}
                    disabled={isGenerating || !prompt.trim()}
                    className="w-full py-3 mt-2 bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 disabled:opacity-50 text-white rounded-lg font-medium shadow-lg flex items-center justify-center gap-2 transition-all active:scale-[0.98]"
                >
                    {isGenerating ? <Loader2 className="animate-spin" size={18} /> : <Sparkles size={18} />}
                    {isGenerating ? '正在生成...' : '生成资源'}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssetImportModal;
